import React from 'react';
import './CustomButton.css';
import type { CustomButtonType } from './types/CustomButtonType';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * CustomButton Component
 * A highly customizable button that supports multiple variants, sizes, and icon positions.
 * Fully compatible with the tenant-based theme system.
 */
const CustomButton: React.FC<CustomButtonType> = ({
    label,
    iconSrc,
    faIcon,
    type = 'button',
    onClick,
    style,
    borderRadius = '8px',
    iconStyle = {},
    backgroundColor,
    textColor,
    className = '',
    iconPosition = 'right',
    fontSize = '14px',
    fontWeight = '500',
    width = '',
    height = '',
    disabled = false,
    variant = 'filled',
    size = 'medium',
    color = 'primary',
    loading = false,
    fullWidth = false,
    faIconProps = {},
    children,
}) => {
    // Determine the variant class
    const variantClass = variant ? variant : 'filled';

    // Determine the size class
    const sizeClass = size ? size : 'medium';

    // Determine the color class
    const colorClass = color ? color : 'primary';

    // Determine if the button should be full width
    const fullWidthClass = fullWidth ? 'full-width' : '';

    // Handle click with loading state
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!loading && onClick) {
            onClick(event);
        }
    };

    // Determine text color based on variant
    const getTextColor = () => {
        if (variant === 'filled') {
            return textColor || 'var(--crystal-white)';
        } else if (variant === 'outline' || variant === 'text') {
            return backgroundColor || textColor || `var(--crystal-${color})`; 
        }
        return textColor || 'var(--crystal-white)';
    };

    // Helper to render icon or loader in the correct position
    const renderVisual = (position: 'left' | 'right') => {
        if (iconPosition !== position) return null;

        if (loading) {
            return (
                <div className={`custom-button-loader ${colorClass}`} style={{
                    borderTopColor: variant === 'filled' ? (textColor || 'var(--crystal-white)') : (backgroundColor || `var(--crystal-${color})`),
                    marginRight: position === 'left' && (label || children) ? '10px' : '0',
                    marginLeft: position === 'right' && (label || children) ? '10px' : '0',
                    ...iconStyle
                }} />
            );
        }

        return (
            <>
                {faIcon && (
                    <FontAwesomeIcon
                        icon={faIcon}
                        className={`button-icon ${position}`}
                        style={{ 
                            ...iconStyle, 
                            marginRight: position === 'left' && (label || children) ? '8px' : '0',
                            marginLeft: position === 'right' && (label || children) ? '8px' : '0'
                        }}
                        {...faIconProps}
                    />
                )}
                {iconSrc && (
                    <img
                        src={iconSrc}
                        alt="button-icon"
                        className={`button-icon ${position}`}
                        style={{ 
                            ...iconStyle, 
                            marginRight: position === 'left' && (label || children) ? '8px' : '0',
                            marginLeft: position === 'right' && (label || children) ? '8px' : '0'
                        }}
                    />
                )}
            </>
        );
    };

    return (
        <button
            className={`custom-button ${variantClass} ${sizeClass} ${colorClass} ${fullWidthClass} ${className}`}
            type={type}
            onClick={handleClick}
            disabled={disabled || loading}
            style={{
                borderRadius,
                backgroundColor: variant === 'filled' ? (backgroundColor || `var(--crystal-${color})`) : 'transparent',
                color: getTextColor(),
                fontWeight: fontWeight,
                fontSize: fontSize,
                width: width,
                height: height,
                borderColor: variant === 'outline' ? (backgroundColor || `var(--crystal-${color})`) : 'transparent',
                opacity: disabled ? 0.6 : 1,
                cursor: disabled || loading ? 'not-allowed' : 'pointer',
                justifyContent: loading ? 'center' : undefined,
                ...style,
            }}
        >
            {renderVisual('left')}
            {label || children}
            {renderVisual('right')}
        </button>
    );
};

export default CustomButton;
